import { useState, useEffect, useCallback, useRef } from "react";
import {
  getSliderlist,
  normalizeSliderListResponse,
  toggleSliderStatus,
  deleteSlider,
  reorderSlider,
} from "../actions/home.actions";
import { store } from "../store"; // Make sure this path is correct to your Redux store
import {
  reorderList,
  reorderSliderList,
} from "../utils/reorderList";

export { reorderList, reorderSliderList };

/**
 * Custom hook for handling slider data with error handling to prevent infinite loops
 */
export const useSliderData = (initialParams = {}) => {
  // eslint-disable-next-line no-unused-vars
  const getAuthState = () => store.getState().auth || {};
  // eslint-disable-next-line no-unused-vars
  const getUserState = () => store.getState().user || {};
  const getIsAuthenticated = () =>
    Boolean(store.getState().auth?.isAuthenticated);

  const isAuthenticated = getIsAuthenticated();
  // ref to track the request status
  const requestInProgressRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const initialFetchDoneRef = useRef(false);

  // The rest of your state declarations
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [toggleLoading, setToggleLoading] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(null);
  const [errorCount, setErrorCount] = useState(0); // Track error count to prevent infinite loops

  const applySliderListResponse = useCallback((response) => {
    const normalized = normalizeSliderListResponse(response);

    if (!normalized) {
      return null;
    }

    setData(normalized.data);
    setTotalCount(normalized.totalCount);
    setTotalPage(normalized.totalPage);
    setErrorCount(0);
    setError(null);

    return normalized;
  }, []);

  // Search state
  const [search, setSearch] = useState({
    text: initialParams.searchText || "",
    type: initialParams.searchType || "lebel",
  });

  // Current pagination state
  const [pagination, setPagination] = useState({
    pageNo: initialParams.pageNo || 1,
    perPage: initialParams.perPage || 50,
  });

  // Fetch data function with error handling and rate limiting
  const fetchData = useCallback(
    async (overrideParams = {}) => {
      const force = overrideParams.force === true;

      // If already had 3+ consecutive errors, don't retry. Leave the hook
      // available for an explicit page refresh instead of locking the request
      // guard in the in-progress state.
      if (errorCount >= 3 && !force) {
        log.error("Too many consecutive errors, stopping retries");
        return;
      }

      // CRITICAL FIX: Prevent multiple simultaneous requests
      if (requestInProgressRef.current) {
        log.debug("Request already in progress, skipping duplicate request");
        return;
      }

      // RATE LIMITING: Enforce a minimum delay between requests (1.5 seconds)
      const now = Date.now();
      const timeSinceLastRequest = now - lastFetchTimeRef.current;
      if (!force && timeSinceLastRequest < 1500) {
        log.debug(
          `Rate limiting: Waiting for cooldown (${timeSinceLastRequest}ms since last request)`,
        );
        return;
      }

      // Update tracking variables
      requestInProgressRef.current = true;
      lastFetchTimeRef.current = now;

      setLoading(true);

      try {
        const params = {
          pageNo: overrideParams.pageNo || pagination.pageNo,
          perPage: overrideParams.perPage || pagination.perPage,
          searchText:
            overrideParams.searchText !== undefined
              ? overrideParams.searchText
              : search.text,
          searchType: overrideParams.searchType || search.type,
        };

        // Update pagination state if values are different
        if (
          params.pageNo !== pagination.pageNo ||
          params.perPage !== pagination.perPage
        ) {
          setPagination({
            pageNo: params.pageNo,
            perPage: params.perPage,
          });
        }

        // Log what we're requesting to help debug
        log.debug("Fetching slider data with params:", params);

        const response = await getSliderlist(params);

        const normalized = applySliderListResponse(response);
        if (!normalized) {
          throw new Error("Invalid response format");
        }

        return normalized;
      } catch (err) {
        log.error("Failed to fetch slider data:", err);

        // Increment error count to prevent infinite loops
        setErrorCount((prev) => prev + 1);
        setError(err.message || "Failed to load data");

        // Set empty data to prevent using stale data
        setData([]);
      } finally {
        setLoading(false);
        requestInProgressRef.current = false;
      }
    },
    [applySliderListResponse, pagination, search, errorCount],
  );

  // Toggle slider status
  const toggleStatus = useCallback(
    async (id, active) => {
      setToggleLoading(id);

      try {
        const response = await toggleSliderStatus({
          slider_id: id,
          slider_status: active ? "active" : "inactive",
        });

        if (response && response.data && response.data.status) {
          // Reflect the committed server value immediately. fetchData() may
          // be rate-limited after the list request, which previously made a
          // successful toggle look broken until the page was reloaded.
          const committedStatus =
            response.data.data?.slider_status ||
            (active ? "active" : "inactive");
          setData((currentData) =>
            currentData.map((slider) =>
              slider.slider_id === id
                ? { ...slider, slider_status: committedStatus }
                : slider,
            ),
          );
        } else {
          log.error(
            "Error toggling status:",
            response?.data?.message || "Unknown error",
          );
        }
      } catch (err) {
        log.error("Failed to toggle status:", err);
        // Don't retry fetchData() here as it could cause loops
      } finally {
        setToggleLoading(null);
      }
    },
    [],
  );

  // Remove the deleted row locally so the manage table reflects the server
  // response immediately, even when the list request is rate-limited.
  const deleteSliderItem = useCallback(
    async (slider) => {
      const sliderId = slider?.slider_id ?? slider;
      const response = await deleteSlider({ slider_id: sliderId });

      if (response?.data?.status) {
        const nextTotalCount = Math.max(totalCount - 1, 0);
        const nextTotalPage = Math.ceil(nextTotalCount / pagination.perPage);
        const nextPageNo =
          nextTotalPage > 0 ? Math.min(pagination.pageNo, nextTotalPage) : 1;

        // Update immediately, then refill the page so rows from the next page
        // move into the visible page after a deletion.
        const remainingData = data.filter(
          (item) => item.slider_id !== sliderId,
        );
        setData(remainingData);
        setTotalCount(nextTotalCount);
        setTotalPage(nextTotalPage);

        const refreshed = await fetchData({
          pageNo: nextPageNo,
          perPage: pagination.perPage,
          force: true,
        });

        if (!refreshed) {
          setData(remainingData);
          setTotalCount(nextTotalCount);
          setTotalPage(nextTotalPage);
        }
      }

      return response;
    },
    [data, fetchData, pagination.pageNo, pagination.perPage, totalCount],
  );

  const reorderSliderItem = useCallback(
    async (sliderId, direction) => {
      const previousData = data;
      const optimisticData = reorderList(
        data,
        sliderId,
        direction,
        "slider_id",
      );

      if (!data.some((item) => item.slider_id === sliderId)) {
        return null;
      }

      setReorderLoading(sliderId);
      setData(optimisticData);

      try {
        const response = await reorderSlider({
          slider_id: sliderId,
          direction,
        });

        if (!response?.data?.status) {
          throw new Error(
            response?.data?.message || "Banner order could not be saved.",
          );
        }

        // Reconcile edge and page-boundary moves with the server's complete
        // ordered collection after the optimistic update is visible.
        await fetchData({ force: true });

        return response;
      } catch (err) {
        setData(previousData);
        log.error("Failed to reorder slider:", err);
        return null;
      } finally {
        setReorderLoading(null);
      }
    },
    [data, fetchData],
  );

  // Search text change handler
  const setSearchText = useCallback(
    (text) => setSearch((prev) => ({ ...prev, text })),
    [],
  );

  // Search type change handler
  const setSearchType = useCallback(
    (type) => setSearch((prev) => ({ ...prev, type })),
    [],
  );

  // Submit search - triggers data fetch
  const submitSearch = useCallback(
    () => fetchData({ pageNo: 1 }), // Reset to first page on search
    [fetchData],
  );

  // Clear search
  const clearSearch = useCallback(() => {
    setSearch({ text: "", type: "lebel" });
    fetchData({ pageNo: 1, searchText: "", searchType: "lebel" });
  }, [fetchData]);

  // Update only the initialFetch effect to prevent remounting issues
  useEffect(
    () => {
      // Prevent duplicate initial fetches AND remount loops
      if (initialFetchDoneRef.current) {
        return;
      }

      initialFetchDoneRef.current = true;
      let isMounted = true;

      // Use a stable mount check with timeout to break the remount cycle
      const timeoutId = setTimeout(() => {
        const initialFetch = async () => {
          try {
            // Skip if we got unmounted during the timeout
            if (!isMounted) {
              return;
            }

            setLoading(true);
            log.debug("Starting initial slider data fetch after delay...");

            if (!isAuthenticated) {
              log.error("No authentication token found!");
              if (isMounted) {
                setError("Authentication error. Please log in again.");
                setData([]);
                setLoading(false);
              }
              return;
            }

            // Set a flag to track this request
            requestInProgressRef.current = true;
            lastFetchTimeRef.current = Date.now();

            // BACKOFF: Add an initial delay to prevent race conditions
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Make the API request with a longer timeout
            const response = await getSliderlist({
              pageNo: pagination.pageNo,
              perPage: pagination.perPage,
              searchText: search.text,
              searchType: search.type,
            });

            // Only update state if component is still mounted
            if (isMounted) {
              const normalized = applySliderListResponse(response);
              if (normalized) {
                log.debug(
                  `Received ${normalized.data.length} sliders`,
                );
              } else {
                log.warn("Invalid response format:", response);
                setError("Failed to load data - invalid response format");
                setData([]);
              }
            }
          } catch (err) {
            log.error("Initial slider data fetch failed:", err);

            // Check specifically for auth errors
            if (err.response?.status === 401) {
              log.error(
                "Authentication error (401) - token may be invalid or missing",
              );

              if (isMounted) {
                setError("Authentication expired. Please log in again.");
                // Redirect to login after a short delay
                setTimeout(() => {
                  window.location.href = "/";
                }, 2000);
              }
            } else if (isMounted) {
              setError(
                "Failed to load slider data. Please try refreshing the page.",
              );
              setData([]);
            }
          } finally {
            if (isMounted) {
              setLoading(false);
            }
            requestInProgressRef.current = false;
          }
        };

        initialFetch();
      }, 800); //  significant delay to break the cycle

      // Cleanup function to prevent setting state on unmounted component
      return () => {
        isMounted = false;
        clearTimeout(timeoutId);
        log.debug("Slider data hook cleanup - component unmounted");
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      /* dependency array stays empty */
    ],
  );

  return {
    data,
    loading,
    error,
    totalCount,
    totalPage,
    pageNo: pagination.pageNo,
    perPage: pagination.perPage,
    toggleLoading,
    reorderLoading,
    deleteSliderItem,
    fetchData,
    toggleStatus,
    reorderSliderItem,
    search,
    setSearchText,
    setSearchType,
    submitSearch,
    clearSearch,
  };
};

export default useSliderData;
